import Alert from '@/components/alert/Alert';
import { AlertProps } from '@/components/alert/Alert.types';
import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

export interface AlertConfig extends Omit<AlertProps, 'children' | 'onClose'> {
  message?: string;
  onClose?: () => void;
}

interface AlertContextType {
  showAlert: (config: AlertConfig) => void;
  hideAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const clearConfigTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showAlert = useCallback((config: AlertConfig) => {
    if (clearConfigTimeoutRef.current) {
      clearTimeout(clearConfigTimeoutRef.current);
      clearConfigTimeoutRef.current = null;
    }
    setAlertConfig(config);
    setIsVisible(true);
  }, []);

  const hideAlert = useCallback(() => {
    alertConfig?.onClose?.();
    setIsVisible(false);
    if (clearConfigTimeoutRef.current) clearTimeout(clearConfigTimeoutRef.current);
    clearConfigTimeoutRef.current = setTimeout(() => {
      setAlertConfig(null);
      clearConfigTimeoutRef.current = null;
    }, 300);
  }, [alertConfig]);

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert }}>
      {isVisible && alertConfig && <Alert {...alertConfig} onClose={hideAlert} />}
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);

  if (!context) {
    throw new Error('O useAlert deve ser usado dentro de um AlertProvider');
  }

  return context;
};
